import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldX, ArrowLeft, Loader2 } from 'lucide-react';
import { 
  useProducerAccess, 
  type CapabilityKey,
  CAPABILITY_LABELS,
  REQUIRED_ROLE_LABELS,
} from '@/hooks/useProducerAccess';

interface RequireProducerCapabilityProps {
  producerId: string | undefined;
  capability: CapabilityKey;
  children: ReactNode;
  /** Fallback to show while loading (defaults to spinner) */
  loadingFallback?: ReactNode;
  /** If true, show content but disabled for read-only users */
  allowReadOnly?: boolean;
}

/**
 * Guard component that blocks access if user lacks required capability.
 * Shows a "no access" view with option to go back.
 */
export function RequireProducerCapability({
  producerId,
  capability,
  children,
  loadingFallback,
  allowReadOnly = false,
}: RequireProducerCapabilityProps) {
  const navigate = useNavigate();
  const { hasCapability, isLoading, isReadOnly, role } = useProducerAccess(producerId);

  // Loading state
  if (isLoading) {
    return loadingFallback ?? (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // No role at all - should be handled by route guard, but safety fallback
  if (!role) {
    return <NoAccessView capability={capability} onBack={() => navigate('/producer')} />;
  }

  // Check capability
  const hasAccess = hasCapability(capability);

  // If read-only is allowed, viewers can see the content
  if (!hasAccess && !(allowReadOnly && isReadOnly)) {
    return <NoAccessView capability={capability} onBack={() => navigate(-1)} />;
  }

  return <>{children}</>;
}

interface NoAccessViewProps {
  capability: CapabilityKey;
  onBack: () => void;
}

function NoAccessView({ capability, onBack }: NoAccessViewProps) {
  const requiredRole = REQUIRED_ROLE_LABELS[capability];
  const capabilityLabel = CAPABILITY_LABELS[capability];

  return (
    <div className="flex items-center justify-center min-h-[400px] p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <ShieldX className="h-6 w-6 text-muted-foreground" />
          </div>
          <CardTitle>Du saknar behörighet</CardTitle>
          <CardDescription>
            För att {capabilityLabel.toLowerCase()} behöver du rollen{' '}
            <strong>{requiredRole}</strong>.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4">
          <p className="text-sm text-muted-foreground text-center">
            Be en administratör eller ägare att uppgradera din roll om du behöver åtkomst.
          </p>
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tillbaka
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Context provider for read-only mode in a subtree.
 * Components can check this to disable editing features.
 */
export { useProducerAccess } from '@/hooks/useProducerAccess';
